/**
 * People (0.5.0): one tenant record, one profile, one sign-up for every program. Shared by the app and the
 * API: the zod schemas every write is validated with (the same on both sides), the intake's 14 questions in
 * Elijah's order, the mapping of each answer to a column (or to SchedulingNotes when none fits), the ids,
 * and the five eligibility conditions in plain words.
 *
 * Columns are the live internal names in reference/SCHEMA-DIGEST.md; choice values are used exactly as stored.
 */
import { z } from 'zod';
import { visionsInclusion } from './visions';
import { vocab } from './vocab';

/* ------------------------------------------------------------------------------------------------
 * Programs
 * ---------------------------------------------------------------------------------------------- */

export const PROGRAM_KEYS = ['housekeeping', 'reading', 'companion'] as const;
export type ProgramKey = (typeof PROGRAM_KEYS)[number];

/** SM_Programs.SM_Programs — the choice that names each program row (the Program lookup's target). */
export const PROGRAM_CHOICE: Record<ProgramKey, (typeof vocab.SM_Programs.SM_Programs)[number]> = {
  housekeeping: 'Housekeeping',
  reading: 'Reading Services',
  companion: 'Community Companion and Wellness',
};

/** ProgramCode the app looks for first (the choice above is the fallback). */
export const PROGRAM_CODE: Record<ProgramKey, string> = { housekeeping: 'HOUSEKEEPING', reading: 'READING', companion: 'COMPANION' };

/** What each program shows on its switch. */
export type ProgramState = 'active' | 'signingUp' | 'notEligible' | 'paused' | 'left' | 'notEnrolled';

export function programStateOf(enrollmentStatus: string | null, missing: readonly string[]): ProgramState {
  switch (enrollmentStatus) {
    case null:
    case '':
      return 'notEnrolled';
    case 'Paused':
      return 'paused';
    case 'Ended':
      return 'left';
    case 'Not Approved':
      return 'notEligible';
    case 'Active':
      return missing.length ? 'signingUp' : 'active';
    default:
      return 'signingUp'; // Pending Review, Waitlisted
  }
}

/* ------------------------------------------------------------------------------------------------
 * Vocabularies the intake uses (stable keys; the words come from i18n)
 * ---------------------------------------------------------------------------------------------- */

const V = vocab;
const choice = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values);
const without = <T extends string>(values: readonly T[], drop: readonly string[]) => values.filter((v) => !drop.includes(v)) as unknown as readonly [T, ...T[]];

export const UPDATES_BY = ['print', 'braille', 'email', 'text'] as const;
export const HOME_ATTENDANT = ['yes', 'no', 'other'] as const;
export const ATTENDANT_DAYS = ['1to3', '3to4', '4to7', 'care24', 'none'] as const;
export const FREQUENCY_ASKED = ['onceWeek', 'twiceWeek', 'onceMonth', 'twiceMonth'] as const;
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const DAY_PARTS = ['morning', 'afternoon', 'either'] as const;
export const LANGUAGES = ['English', 'Spanish', 'Other'] as const;
export const MOBILITY_AIDS = ['none', 'whiteCane', 'walker', 'rollator', 'wheelchair', 'other'] as const;
export const YES_NO = ['yes', 'no'] as const;
export const YES_NO_NOT_SURE = ['yes', 'no', 'notSure'] as const;
export const PER_CYCLE = ['1', '2', 'noLimit'] as const;
export type PerCycle = (typeof PER_CYCLE)[number];

export const CONTACT_METHODS = V.SM_Tenants.PreferredContactMethod;
export const WEEK_OF_MONTH = V.SM_TenantProgramEnrollments.PrimaryWeekOfMonth;
export const DAY_OF_WEEK = V.SM_TenantProgramEnrollments.PrimaryDayOfWeek;
export const SERVICE_PERIOD = V.SM_TenantProgramEnrollments.PrimaryServicePeriod;
/** How a signed release reached the office (typed signing records Electronic Form). */
/** 1.5.7: the release of someone who was in the program before the app — the paper copy is in ABF's files. */
export const RELEASE_PAPER_BEFORE_APP = 'paper, before the app';
export const WAIVER_METHODS = without(V.SM_TenantProgramEnrollments.HousekeepingWaiverReceivedMethod, ['Not Recorded']);
export const HOLD_CATEGORIES = without(V.SM_TenantProgramEnrollments.ServiceSafetyHoldCategory, ['Not Applicable']);
/** "Inactive" in the prompt: the list's own words are Temporarily Inactive and Moved Out. */
export const TENANT_STATUSES = V.SM_Tenants.TenantStatus;

/**
 * 25 Oct 2026 (D-1025-ADMIN): ONE rule for where a tenant stands. The list has three words (Active, Temporarily
 * Inactive, Moved Out); a fourth state, paused, is an enrollment paused or on a safety hold while the tenant is
 * still Active, so it is only reported when the caller says so. A blank or unknown word is "inactive" — the rule
 * every `status !== 'Active'` check already followed.
 */
export const TENANT_STANDINGS = ['active', 'paused', 'inactive', 'movedOut'] as const;
export type TenantStanding = (typeof TENANT_STANDINGS)[number];

export function tenantStanding(status: string | null | undefined, opts: { paused?: boolean } = {}): TenantStanding {
  if (status === 'Active') return opts.paused ? 'paused' : 'active';
  if (status === 'Moved Out') return 'movedOut';
  return 'inactive';
}
/** Active on the list (a paused enrollment does not change this). */
export const isTenantActive = (status: string | null | undefined): boolean => tenantStanding(status) === 'active';
export const isTenantMovedOut = (status: string | null | undefined): boolean => tenantStanding(status) === 'movedOut';
/** Temporarily Inactive, or a word the list does not know — everyone who is not Active and has not moved out. */
export const isTenantInactive = (status: string | null | undefined): boolean => tenantStanding(status) === 'inactive';

/* ------------------------------------------------------------------------------------------------
 * Field rules
 * ---------------------------------------------------------------------------------------------- */

export const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const isoDay = z.string().regex(ISO_DAY, 'date');
const text = (max: number) => z.string().trim().max(max);
const optText = (max: number) => text(max).optional().default('');

/** A US phone: 10 digits (an optional leading 1); spaces, dashes, dots and brackets allowed. */
export function phoneDigits(s: string): string {
  const d = s.replace(/[^0-9]/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}
export const phone = z
  .string()
  .trim()
  .max(30)
  .refine((s) => phoneDigits(s).length === 10, 'phone');

/** (212) 555-0142 */
export function formatPhone(s: string): string {
  const d = phoneDigits(s);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : s.trim();
}

export const unit = z
  .string()
  .trim()
  .min(1, 'required')
  .max(20)
  .regex(/^[A-Za-z0-9][A-Za-z0-9 -]*$/, 'unit');

const name = z.string().trim().min(1, 'required').max(60);
const email = z.string().trim().max(120).email('email');

/* ------------------------------------------------------------------------------------------------
 * The sign-up: step 2, identity and contact (shared by every program)
 * ---------------------------------------------------------------------------------------------- */

const identityShape = {
  firstName: name,
  lastName: name,
  preferredName: optText(60),
  unit,
  phone,
  email: z.union([z.literal(''), email]).optional().default(''),
  /** Intake Q6 — "How would you like to receive information and updates?" (select all that apply) — required. */
  updatesBy: z.array(choice(UPDATES_BY)).min(1, 'required'),
  preferredContactMethod: choice(CONTACT_METHODS),
  /** Written notice by email: only with an address (email brings the one-press Confirm / different day / Cancel). */
  emailAllowed: z.boolean().default(false),
  language: choice(LANGUAGES),
  languageOther: optText(40),
  /** REQUIREMENTS §15: consent to automated calls and texts about appointments — a required yes/no, dated. */
  automatedConsent: choice(YES_NO),
  automatedConsentDate: isoDay,
};
const identityRules = (v: { emailAllowed: boolean; email: string; language: string; languageOther: string }, ctx: z.RefinementCtx) => {
  if (v.emailAllowed && !v.email) ctx.addIssue({ code: 'custom', path: ['email'], message: 'emailForNotice' });
  if (v.language === 'Other' && !v.languageOther) ctx.addIssue({ code: 'custom', path: ['languageOther'], message: 'required' });
};

/** The sign-up's identity step: "updates by" and the automated-calls consent are REQUIRED for a new person. */
export const IdentitySchema = z.object(identityShape).superRefine(identityRules);

/**
 * Editing an older tenant (Eli, 22 Oct 2026, D-1022-TENANT3): "updates by" and the automated-calls consent are optional when
 * they were never recorded. Left empty they are not saved (the stored values, if any, stay as they are); the screen still
 * asks for them when the person already has an answer, so an answer is never blanked by accident.
 */
export const IdentityEditSchema = z
  .object({
    ...identityShape,
    updatesBy: z.array(choice(UPDATES_BY)).default([]),
    automatedConsent: z.union([z.literal(''), choice(YES_NO)]).optional().default(''),
    automatedConsentDate: z.union([z.literal(''), isoDay]).optional().default(''),
  })
  .superRefine(identityRules);
export type IdentityEdit = z.infer<typeof IdentityEditSchema>;
export type Identity = z.infer<typeof IdentitySchema>;

/* ------------------------------------------------------------------------------------------------
 * Housekeeping: the intake (Elijah's 14 questions, same order), the program, the pattern
 * ---------------------------------------------------------------------------------------------- */

export const IntakeSchema = z.object({
  /** Q1 Date * */
  intakeDate: isoDay,
  /** Q7 Do you currently have a home attendant or any other cleaning assistance? * */
  homeAttendant: choice(HOME_ATTENDANT),
  homeAttendantOther: optText(200),
  /** Q8 If you have a home attendant, how many days a week do they assist you? * */
  attendantDays: choice(ATTENDANT_DAYS),
  /** Q9 How many times … would you like to have someone come and assist you with cleaning? * — recorded as asked. */
  frequencyAsked: choice(FREQUENCY_ASKED),
  /** Q10 Belongings or areas to avoid or handle with extra care */
  avoid: optText(1000),
  /** Q11 Do you have any pets in your home? */
  pets: z.union([z.literal(''), choice(YES_NO)]).optional().default(''),
  /** Q12 What days and times are most convenient? (select all that apply) */
  convenientDays: z.array(choice(WEEKDAYS)).default([]),
  convenientTime: z.union([z.literal(''), choice(DAY_PARTS)]).optional().default(''),
  /** Q13 Cleaning-product preferences or requirements (allergies or sensitivities) */
  products: optText(1000),
  /** Q14 Open to giving feedback and suggestions? */
  feedback: optText(1000),
});
export type Intake = z.infer<typeof IntakeSchema>;

export const PatternSchema = z.object({
  primaryWeek: z.union([z.literal(''), choice(WEEK_OF_MONTH)]).default(''),
  primaryDay: z.union([z.literal(''), choice(DAY_OF_WEEK)]).default(''),
  primaryPeriod: z.union([z.literal(''), choice(SERVICE_PERIOD)]).default(''),
  secondaryWeek: z.union([z.literal(''), choice(WEEK_OF_MONTH)]).default(''),
  secondaryDay: z.union([z.literal(''), choice(DAY_OF_WEEK)]).default(''),
  secondaryPeriod: z.union([z.literal(''), choice(SERVICE_PERIOD)]).default(''),
});
export type Pattern = z.infer<typeof PatternSchema>;

export const HousekeepingSchema = z.object({
  /** 1 or 2 per cycle, or No Fixed Limit (administrators only — the server refuses it otherwise). */
  perCycle: choice(PER_CYCLE),
  pattern: PatternSchema.default({}),
  /** Access and scheduling notes (SM_Tenants.SchedulingNotes). */
  accessNotes: optText(2000),
  /** PhotoReleaseOnFile and its date; the release itself carries the photo clause. */
  photoRelease: z.boolean().default(false),
  photoReleaseDate: z.union([z.literal(''), isoDay]).optional().default(''),
});
export type HousekeepingAnswers = z.infer<typeof HousekeepingSchema>;

/* ------------------------------------------------------------------------------------------------
 * Companion & Wellness: emergency contact, mobility aid, the four VISIONS questions
 * ---------------------------------------------------------------------------------------------- */

export const VisionsSchema = z
  .object({
    /** 1. 60 or older? * */
    age60: choice(YES_NO),
    /** 2. Registered with VISIONS? * ("not sure" counts as no until staff confirm) */
    registered: choice(YES_NO_NOT_SURE),
    /** 3. OK for ABF to share name, contact and participation with VISIONS? * — the consent. */
    consent: choice(YES_NO),
    /** 4. If not registered: connect me with VISIONS? (yes = referral requested) */
    referral: z.union([z.literal(''), choice(YES_NO)]).optional().default(''),
    /** Age, when the tenant gives it (the report's column E; blank = only the 60+ answer goes in F). */
    age: z.number().int().min(18).max(120).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.registered === 'yes' && v.referral === 'yes') ctx.addIssue({ code: 'custom', path: ['referral'], message: 'referralRegistered' });
  });
export type VisionsAnswers = z.infer<typeof VisionsSchema>;

/** Eli, 26 Oct 2026 (D-1026-EMERGENCY): the emergency contact is optional — never required, never a gate. Whatever is given is trimmed and kept; a phone, if given, must still be a valid phone. */
const optPhone = z.union([z.literal(''), phone]).optional().default('');
export const EmergencySchema = z.object({ name: optText(60), phone: optPhone, relation: optText(40) });

export const CompanionSchema = z
  .object({
    emergency: EmergencySchema.optional().default({ name: '', phone: '', relation: '' }),
    mobilityAid: choice(MOBILITY_AIDS),
    mobilityOther: optText(80),
    visions: VisionsSchema,
  })
  .superRefine((v, ctx) => {
    if (v.mobilityAid === 'other' && !v.mobilityOther) ctx.addIssue({ code: 'custom', path: ['mobilityOther'], message: 'required' });
  });
export type CompanionAnswers = z.infer<typeof CompanionSchema>;

/** VISIONS reporting (REQUIREMENTS §12): consent is the gate; with it, registered or 60+ or a referral — the one rule in shared/visions.ts. */
export function onVisionsReport(v: (Pick<VisionsAnswers, 'age60' | 'registered' | 'consent' | 'referral'> & { referralMadeOn?: string | null }) | null | undefined): boolean {
  return visionsInclusion(v).onReport;
}

/* ------------------------------------------------------------------------------------------------
 * The whole sign-up (steps 1–3); documents (step 4) are signed or attached after the records exist
 * ---------------------------------------------------------------------------------------------- */

export const SignupSchema = z
  .object({
    programs: z.array(choice(PROGRAM_KEYS)).min(1, 'pickProgram'),
    identity: IdentitySchema,
    intake: IntakeSchema.optional(),
    housekeeping: HousekeepingSchema.optional(),
    companion: CompanionSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.programs.includes('housekeeping')) {
      if (!v.intake) ctx.addIssue({ code: 'custom', path: ['intake'], message: 'required' });
      if (!v.housekeeping) ctx.addIssue({ code: 'custom', path: ['housekeeping'], message: 'required' });
    }
    if (v.programs.includes('companion') && !v.companion) ctx.addIssue({ code: 'custom', path: ['companion'], message: 'required' });
  });
export type Signup = z.infer<typeof SignupSchema>;
export type SignupInput = z.input<typeof SignupSchema>;

/** Edits on the profile: any part, validated like the sign-up; status Moved Out needs its date. */
export const PersonEditSchema = z
  .object({
    identity: IdentityEditSchema.optional(),
    accessNotes: text(2000).optional(),
    status: choice(TENANT_STATUSES).optional(),
    moveOutDate: z.union([z.literal(''), isoDay]).optional(),
    housekeeping: z.object({ perCycle: choice(PER_CYCLE), pattern: PatternSchema, reason: optText(300) }).partial().optional(),
    companion: z
      .object({ emergency: EmergencySchema, mobilityAid: choice(MOBILITY_AIDS), mobilityOther: optText(80) })
      .partial()
      .optional(),
    visions: VisionsSchema.optional(),
    photoRelease: z.object({ onFile: z.boolean(), date: z.union([z.literal(''), isoDay]) }).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'Moved Out' && !v.moveOutDate) ctx.addIssue({ code: 'custom', path: ['moveOutDate'], message: 'required' });
  });
export type PersonEdit = z.infer<typeof PersonEditSchema>;

/** Ticking a program on (starts only what is missing) or off (a reason and a date; history kept). */
export const ProgramSwitchSchema = z
  .object({
    program: choice(PROGRAM_KEYS),
    on: z.boolean(),
    reason: optText(300),
    date: z.union([z.literal(''), isoDay]).optional().default(''),
    /** Off: Paused (coming back) or Ended (left the program). */
    kind: z.enum(['pause', 'leave']).optional().default('leave'),
    housekeeping: HousekeepingSchema.optional(),
    intake: IntakeSchema.optional(),
    companion: CompanionSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.on && !v.reason) ctx.addIssue({ code: 'custom', path: ['reason'], message: 'required' });
    if (!v.on && !v.date) ctx.addIssue({ code: 'custom', path: ['date'], message: 'required' });
  });
export type ProgramSwitch = z.infer<typeof ProgramSwitchSchema>;

/* ------------------------------------------------------------------------------------------------
 * Documents: typed signing, attaching the signed paper, the home visit, the safety hold
 * ---------------------------------------------------------------------------------------------- */

export const SIGN_DOC_KEYS = ['housekeeping-release', 'companion-waiver'] as const;

export const SignSchema = z.object({
  doc: choice(SIGN_DOC_KEYS),
  lang: z.enum(['en', 'es']),
  /** sha256 of the text shown (the server checks it against its own copy). */
  hash: z.string().regex(/^[0-9a-f]{64}$/i, 'hash'),
  typedName: z.string().trim().min(2, 'typedName').max(120),
  agree: z.literal(true, { errorMap: () => ({ message: 'agree' }) }),
});
export type SignRequest = z.infer<typeof SignSchema>;

/** Attach the signed paper (multipart fields beside the file). */
export const AttachSchema = z.object({
  doc: choice(SIGN_DOC_KEYS),
  method: choice(WAIVER_METHODS),
  signedDate: isoDay,
});
export type AttachRequest = z.infer<typeof AttachSchema>;
export const ATTACH_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_ATTACH_BYTES = 15 * 1024 * 1024;

export const HomeVisitSchema = z.object({
  date: isoDay,
  /** Who made the visit (a name; the signed-in person by default). */
  completedBy: z.string().trim().min(2, 'required').max(120),
  notes: optText(4000),
  /** What they get is set at the home visit by staff, with the reason (REQUIREMENTS §3). */
  perCycle: choice(PER_CYCLE).optional(),
  perCycleReason: optText(300),
});
export type HomeVisit = z.infer<typeof HomeVisitSchema>;

export const SafetyHoldSchema = z
  .object({
    active: z.boolean(),
    category: z.union([z.literal(''), choice(HOLD_CATEGORIES)]).default(''),
    start: z.union([z.literal(''), isoDay]).default(''),
    end: z.union([z.literal(''), isoDay]).default(''),
    notes: optText(2000),
  })
  .superRefine((v, ctx) => {
    if (v.active && !v.category) ctx.addIssue({ code: 'custom', path: ['category'], message: 'required' });
    if (v.active && !v.start) ctx.addIssue({ code: 'custom', path: ['start'], message: 'required' });
    if (v.start && v.end && v.end < v.start) ctx.addIssue({ code: 'custom', path: ['end'], message: 'endBeforeStart' });
  });
export type SafetyHold = z.infer<typeof SafetyHoldSchema>;

/** Add document / classify a dropped-in file. */
export const DOC_KINDS = ['housekeeping-release', 'companion-waiver', 'intake', 'home-visit', 'feedback', 'other'] as const;
export type DocKind = (typeof DOC_KINDS)[number];
export const ClassifySchema = z.object({ name: z.string().trim().min(1).max(200), kind: choice(DOC_KINDS), label: optText(80) });

export const EmailCopySchema = z.object({ doc: z.enum([...SIGN_DOC_KEYS, 'packet']), to: email, lang: z.enum(['en', 'es']).default('en') });

/* ------------------------------------------------------------------------------------------------
 * Ids — the live SECURITY-TEST rows show the convention (reference/fixtures.js mirrors them exactly):
 * `SECURITY-TEST-TENANT-00001`, `SECURITY-TEST-ENR-00001` — a word and a five-digit sequence, the test
 * prefix on test people only. The app follows it: TENANT-00042 / ENR-00043 (docs/DECISIONS.md D-0.5.0-2).
 * ---------------------------------------------------------------------------------------------- */

export const TEST_PREFIX = 'SECURITY-TEST';

export function isTestPerson(firstName: string, lastName: string): boolean {
  return `${firstName} ${lastName}`.toUpperCase().includes(TEST_PREFIX);
}

/** The next id in a sequence: the highest existing number + 1 (+ `bump` on a unique-key retry). */
export function nextSequenceId(kind: 'TENANT' | 'ENR', existing: readonly string[], test: boolean, bump = 0): string {
  const re = new RegExp(`(?:^|-)${kind}-(\\d+)$`);
  const max = existing.reduce((m, id) => {
    const hit = re.exec(id.trim());
    return hit ? Math.max(m, Number(hit[1])) : m;
  }, 0);
  const n = String(max + 1 + bump).padStart(5, '0');
  return `${test ? `${TEST_PREFIX}-` : ''}${kind}-${n}`;
}

/* ------------------------------------------------------------------------------------------------
 * The intake → columns (docs/DECISIONS.md D-0.5.0-3 lists what has no column)
 * ---------------------------------------------------------------------------------------------- */

/** How many visits per cycle the tenant asked for, as a starting point for staff (what they get is set at the home visit). */
export function suggestedPerCycle(asked: Intake['frequencyAsked']): PerCycle {
  return asked === 'onceMonth' ? '1' : '2';
}

export const INTAKE_LABELS_EN: Record<string, string> = {
  intakeDate: 'Date',
  homeAttendant: 'Do you currently have a home attendant or any other cleaning assistance?',
  attendantDays: 'If you have a home attendant, how many days a week do they assist you?',
  frequencyAsked: 'How many times would you like to have someone come and assist you with cleaning?',
  avoid: 'Are there any specific belongings or areas in your home that you would prefer our cleaning team to avoid or handle with extra care?',
  pets: 'Do you have any pets in your home?',
  convenientDays: 'What days and times are most convenient for you to have cleaning services scheduled?',
  products: 'Do you have any preferences or specific requirements regarding the type of cleaning products the team should use, particularly in consideration of any allergies or sensitivities?',
  feedback: 'Would you be open to providing feedback and suggestions on the cleaning services offered?',
};

const ANSWER_EN: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  other: 'Other',
  '1to3': '1 to 3',
  '3to4': '3 to 4',
  '4to7': '4 to 7',
  care24: 'I have 24 hour care.',
  none: 'I do not have any assistance with cleaning',
  onceWeek: 'Once a week',
  twiceWeek: 'Twice a week',
  onceMonth: 'Once a month',
  twiceMonth: 'Twice a month',
  morning: 'mornings',
  afternoon: 'afternoons',
  either: 'morning or afternoon',
  print: 'Print',
  braille: 'Braille',
  email: 'Email',
  text: 'Text Messages',
};
export const answerEn = (v: string) => ANSWER_EN[v] ?? v;

/** The intake block written to the Housekeeping enrollment's SchedulingNotes — the answers that have no column, in English, as asked. */
export function intakeNotes(i: Intake, by: string): string {
  const lines = [`Intake ${i.intakeDate} (recorded by ${by}):`];
  const add = (k: string, v: string) => {
    if (v.trim()) lines.push(`- ${INTAKE_LABELS_EN[k]} ${v.trim()}`);
  };
  add('homeAttendant', i.homeAttendant === 'other' && i.homeAttendantOther ? `Other: ${i.homeAttendantOther}` : answerEn(i.homeAttendant));
  add('attendantDays', answerEn(i.attendantDays));
  add('frequencyAsked', `${answerEn(i.frequencyAsked)} (as asked; what they get is set at the home visit)`);
  add('avoid', i.avoid);
  add('pets', i.pets ? answerEn(i.pets) : '');
  add('convenientDays', [i.convenientDays.join(', '), i.convenientTime ? answerEn(i.convenientTime) : ''].filter(Boolean).join(' · '));
  add('products', i.products);
  add('feedback', i.feedback);
  return lines.join('\n');
}

/** The automated-calls consent line kept in SM_Tenants.SchedulingNotes (no column fits — D-0.5.0-4). */
export function consentLine(answer: 'yes' | 'no', date: string): string {
  return `Automated calls and texts about appointments: ${answer === 'yes' ? 'Yes' : 'No'} (${date})`;
}
export const CONSENT_LINE_RE = /^Automated calls and texts about appointments: (?:Yes|No) \(\d{4}-\d{2}-\d{2}\)$/m;

/** SchedulingNotes = the access notes, then the consent line (replaced in place on an edit). */
export function tenantNotes(accessNotes: string, consent: { answer: 'yes' | 'no'; date: string } | null): string {
  const base = accessNotes.replace(CONSENT_LINE_RE, '').trim();
  return [base, consent ? consentLine(consent.answer, consent.date) : ''].filter(Boolean).join('\n\n');
}

/** SM_Tenants columns from the identity step. */
export function tenantFieldsOf(id: Identity | IdentityEdit): Record<string, unknown> {
  return {
    FirstName: id.firstName,
    LastName: id.lastName,
    PreferredName: id.preferredName || null,
    UnitNumber: id.unit.toUpperCase(),
    PhoneNumber: formatPhone(id.phone),
    EmailAddress: id.email || null,
    PreferredContactMethod: id.preferredContactMethod,
    EmailCommunicationAllowed: !!id.email && id.emailAllowed,
    TextMessageAllowed: id.updatesBy.includes('text'),
  };
}

export function perCycleFields(perCycle: PerCycle): Record<string, unknown> {
  return perCycle === 'noLimit'
    ? { AuthorizationBasis: 'No Fixed Limit', AuthorizedAppointmentsPerCycle: null }
    : { AuthorizationBasis: 'Authorization Period', AuthorizedAppointmentsPerCycle: Number(perCycle) };
}

export function patternFields(p: Pattern): Record<string, unknown> {
  const v = (s: string) => s || null;
  return {
    PrimaryWeekOfMonth: v(p.primaryWeek),
    PrimaryDayOfWeek: v(p.primaryDay),
    PrimaryServicePeriod: v(p.primaryPeriod),
    SecondaryWeekOfMonth: v(p.secondaryWeek),
    SecondaryDayOfWeek: v(p.secondaryDay),
    SecondaryServicePeriod: v(p.secondaryPeriod),
  };
}

/* ------------------------------------------------------------------------------------------------
 * Eligibility — the five conditions (REQUIREMENTS §3), in the order the panel shows them
 * ---------------------------------------------------------------------------------------------- */

export const CONDITIONS = ['tenantActive', 'enrollmentActive', 'waiver', 'homeVisit', 'noHold'] as const;
export type Condition = (typeof CONDITIONS)[number];

export interface ConditionState {
  key: Condition;
  met: boolean;
  /** The step that fixes it (the panel's jump). */
  step: 'identity' | 'program' | 'waiver' | 'homeVisit' | 'hold';
  detail?: string | null;
}

export interface EligibilityFacts {
  tenantStatus: string;
  enrollmentStatus: string | null;
  waiver: boolean;
  homeVisit: boolean;
  hold: boolean;
}

/**
 * A Pending Review enrollment (made by the sign-up) becomes Active on its own once the tenant is active, the
 * release and the home visit are in and no hold is on (D-0.5.0-6); Paused, Ended, Waitlisted and Not
 * Approved are a person's decision and stay as they are.
 */
export function effectiveEnrollmentStatus(f: EligibilityFacts): string | null {
  const gatesGreen = f.tenantStatus === 'Active' && f.waiver && f.homeVisit && !f.hold;
  return f.enrollmentStatus === 'Pending Review' && gatesGreen ? 'Active' : f.enrollmentStatus;
}

export function conditionsOf(f: EligibilityFacts): ConditionState[] {
  const enrOk = effectiveEnrollmentStatus(f) === 'Active';
  return [
    { key: 'tenantActive', met: f.tenantStatus === 'Active', step: 'identity', detail: f.tenantStatus || null },
    { key: 'enrollmentActive', met: enrOk, step: 'program', detail: f.enrollmentStatus },
    { key: 'waiver', met: f.waiver, step: 'waiver' },
    { key: 'homeVisit', met: f.homeVisit, step: 'homeVisit' },
    { key: 'noHold', met: !f.hold, step: 'hold' },
  ];
}

/** What the app writes on the enrollment for these facts (EligibilityStatus, EnrollmentStatus, QueueStatus, reason). */
export function eligibilityWrite(f: EligibilityFacts): { eligible: boolean; fields: Record<string, unknown> } {
  const missing = conditionsOf(f).filter((x) => !x.met);
  const eligible = missing.length === 0;
  const reasonEn: Record<Condition, string> = {
    tenantActive: `the tenant record is not Active (${f.tenantStatus || 'blank'})`,
    enrollmentActive: f.enrollmentStatus === 'Pending Review' ? 'the enrollment waits for the release and the home visit' : `the Housekeeping enrollment is ${f.enrollmentStatus || 'missing'}`,
    waiver: 'the release (rev. 11-14-24) is not on file',
    homeVisit: 'the home visit is not recorded',
    noHold: 'a service safety hold is active',
  };
  const fields: Record<string, unknown> = {
    EligibilityStatus: eligible ? 'Eligible' : f.hold ? 'Temporarily Ineligible' : 'Pending Documentation',
    EligibilityReason: eligible ? 'All five conditions met (active tenant, active enrollment, release, home visit, no safety hold).' : `Not yet: ${missing.map((m) => reasonEn[m.key]).join('; ')}.`,
  };
  if (eligible && f.enrollmentStatus === 'Pending Review') fields.EnrollmentStatus = 'Active';
  if (f.enrollmentStatus === 'Active' || f.enrollmentStatus === 'Pending Review') fields.QueueStatus = eligible ? 'Ready for Proposal Generation' : f.hold ? 'On Hold' : 'Not Ready';
  return { eligible, fields };
}

/** The Companion gates (the switch turns Active only when all are met; the waiver above all). */
/** D-1026-EMERGENCY: the emergency contact is not a gate. */
export const COMPANION_GATES = ['waiver', 'mobility', 'visions'] as const;
export type CompanionGate = (typeof COMPANION_GATES)[number];

export function companionMissing(p: { waiverAgreed: boolean; emergency?: boolean; mobility: boolean; visions: boolean }): CompanionGate[] {
  const out: CompanionGate[] = [];
  if (!p.waiverAgreed) out.push('waiver');
  if (!p.mobility) out.push('mobility');
  if (!p.visions) out.push('visions');
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * Categories (1.4.0 §4): TenantCategory `Staff` tags a staff unit and the two offices (downstairs; ABF suite
 * 1209) — each takes a Housekeeping position and is never counted as a tenant anywhere (program numbers,
 * VISIONS, ABF's participation, outings, event households). A facility space is not a tenant row at all.
 * ---------------------------------------------------------------------------------------------- */

export const STAFF_CATEGORY = 'Staff';
export const isStaffCategory = (category: string | null | undefined): boolean => category === STAFF_CATEGORY;

/* ------------------------------------------------------------------------------------------------
 * The People list's filters
 * ---------------------------------------------------------------------------------------------- */

export const PEOPLE_FILTERS = ['all', 'eligible', 'missingWaiver', 'missingHomeVisit', 'onHold', 'inactive', 'missingSomething'] as const;
export type PeopleFilter = (typeof PEOPLE_FILTERS)[number];

export interface PersonRowFacts {
  status: string;
  category: string;
  eligible: boolean;
  hk: { enrolled: boolean; waiver: boolean; homeVisit: boolean; hold: boolean } | null;
  missingCount: number;
}

export function matchesFilter(p: PersonRowFacts, f: PeopleFilter): boolean {
  // 1.5.15: a staff unit or an office has no release, home visit or hold to be missing — it is in no tenant filter
  if (isStaffCategory(p.category) && f !== 'all' && f !== 'inactive') return false;
  switch (f) {
    case 'all':
      return true;
    case 'eligible':
      return p.eligible;
    case 'missingWaiver':
      return !!p.hk?.enrolled && !p.hk.waiver;
    case 'missingHomeVisit':
      return !!p.hk?.enrolled && !p.hk.homeVisit;
    case 'onHold':
      return !!p.hk?.hold;
    case 'inactive':
      return !isTenantActive(p.status);
    case 'missingSomething':
      return isTenantActive(p.status) && p.missingCount > 0;
  }
}

/** "caroline.zhi@abfselis.org" → "Caroline Zhi" (a readable name for footers and notes). */
export function staffName(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  return parts.length ? parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') : email;
}

/** Eli, 16 Oct 2026: the administrator marks Housekeeping complete for a tenant who finished it outside the app. */
export const HousekeepingCompleteSchema = z.object({
  reason: z.string().trim().min(3, 'Please say why (at least a few words).').max(300),
  homeVisitDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .or(z.literal('').transform(() => undefined)),
});
export type HousekeepingComplete = z.input<typeof HousekeepingCompleteSchema>;
